// src/layout/builders/status/StatusChangePopup.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon as WarningIcon,
} from "@heroicons/react/24/outline";
import statusClasses from "./statusClasses";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import ConfirmationPopup from "../../../components/common/ConfirmationPopup"; // Import ConfirmationPopup

const StatusChangePopup = ({
  isOpen,
  onClose,
  currentStatus,
  availableStatuses = [],
  projectId,
  onConfirm, // Function passed from StatusBadge
  isLoading = false, // Loading state for the API call (passed from StatusBadge)
}) => {
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [commentOrReason, setCommentOrReason] = useState("");
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [error, setError] = useState("");

  // --- Confirmation Popup State ---
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
  const [confirmPopupMessage, setConfirmPopupMessage] = useState("");
  const [statusToConfirm, setStatusToConfirm] = useState(null); // Store status temporarily
  const [commentForConfirm, setCommentForConfirm] = useState(""); // Store comment temporarily
  // --- End Confirmation Popup State ---

  // Reset state when the main popup opens
  useEffect(() => {
    if (isOpen) {
      setSelectedStatus(null);
      setCommentOrReason("");
      setShowCommentBox(false);
      setError("");
      setStatusToConfirm(null); // Reset temporary status
      setCommentForConfirm(""); // Reset temporary comment
    }
  }, [isOpen]);

  const formatStatusLabel = (key) => {
    // ... (formatStatusLabel remains the same)
    if (!key) return "";
    return key
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleStatusClick = (statusKey) => {
    setSelectedStatus(statusKey);
    setShowCommentBox(statusKey === "hold" || statusKey === "rejected");
    setError("");
    // Clear comment if switching away from hold/reject
    if (statusKey !== "hold" && statusKey !== "rejected") {
        setCommentOrReason("");
    }
  };

  // --- Modified handleConfirm ---
  const handleConfirm = async () => {
    setError("");

    if (!selectedStatus) {
      setError("Please select a status.");
      return;
    }

    if (
      (selectedStatus === "hold" || selectedStatus === "rejected") &&
      !commentOrReason.trim()
    ) {
      const requiredField = selectedStatus === "hold" ? "Hold comment" : "Rejection reason";
      setError(`A ${requiredField} is required for the "${formatStatusLabel(selectedStatus)}" status.`);
      return;
    }

    // --- Check if confirmation is needed ---
    if (selectedStatus === 'rejected' || selectedStatus === 'completion_request') {
        setStatusToConfirm(selectedStatus); // Store status and comment
        setCommentForConfirm(commentOrReason);
        setConfirmPopupMessage(
            selectedStatus === 'rejected'
            ? `Rejecting this project cannot be easily undone. Are you sure you want to proceed?`
            : `Are you sure you want to request project completion? Ensure all documents are uploaded.`
        );
        setIsConfirmPopupOpen(true); // Open the confirmation popup
        // --- Do not call the original onConfirm yet ---
    } else {
      // --- For other statuses, call original onConfirm directly ---
      await onConfirm(projectId, selectedStatus, commentOrReason);
      // Parent (StatusBadge -> LiveTable) will handle closing and potential errors/redirects
    }
  };

  // --- Function called by ConfirmationPopup ---
  const handleFinalConfirmStatus = async () => {
    setIsConfirmPopupOpen(false); // Close confirmation
    if (statusToConfirm) {
      // Call the original onConfirm passed from StatusBadge
      await onConfirm(projectId, statusToConfirm, commentForConfirm);
      // Parent (StatusBadge -> LiveTable) will handle closing and potential errors/redirects
    }
    // Reset temporary state
    setStatusToConfirm(null);
    setCommentForConfirm("");
  };


  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isLoading && !isConfirmPopupOpen) { // Don't close if confirmation is open
      onClose();
    }
  };

  const isConfirmButtonDisabled =
    !selectedStatus ||
    isLoading || // Disable if parent is loading (API call in progress)
    ((selectedStatus === "hold" || selectedStatus === "rejected") && !commentOrReason.trim());

  const commentLabel = selectedStatus === "hold" ? "Reason for Holding" : "Reason for Rejection";
  const commentPlaceholder = selectedStatus === "hold" ? "Enter reason for putting project on hold..." : "Enter detailed reason for rejecting the project...";

  return (
    <>
      {/* Confirmation Popup (rendered conditionally) */}
      <ConfirmationPopup
        isOpen={isConfirmPopupOpen}
        onClose={() => setIsConfirmPopupOpen(false)}
        onConfirm={handleFinalConfirmStatus}
        message={confirmPopupMessage}
        title={`Confirm ${formatStatusLabel(statusToConfirm || '')}`}
        confirmText="Yes, Proceed"
        isLoading={isLoading} // Pass parent's loading state
      />

      {/* Main Status Change Popup */}
      <AnimatePresence>
        {isOpen && !isConfirmPopupOpen && ( // Render main popup only if open and confirmation is NOT open
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
            onClick={handleOverlayClick}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="bg-white rounded-xl shadow-2xl p-5 sm:p-6 max-w-md w-full relative text-sm"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                aria-label="Close popup"
                disabled={isLoading}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">
                Change Project Status
              </h3>

              {/* Current Status Display */}
               <div className="mb-4 text-center text-xs text-gray-500">
                   Current Status:
                   {statusClasses[currentStatus] ? (
                   <span
                       className={`ml-1 inline-flex items-center px-2 py-0.5 rounded-full font-medium ${statusClasses[currentStatus].bg} ${statusClasses[currentStatus].text}`}
                   >
                       <span
                       className={`h-1.5 w-1.5 rounded-full ${statusClasses[currentStatus].dot} mr-1`}
                       ></span>
                       {formatStatusLabel(currentStatus)}
                   </span>
                   ) : (
                   <span className="ml-1 font-medium text-gray-700">
                       {formatStatusLabel(currentStatus)}
                   </span>
                   )}
               </div>

              {/* Status Selection Buttons */}
               <p className="text-xs text-gray-500 mb-2 text-center">
                   Select new status:
               </p>
               <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                   {availableStatuses.map((statusKey) => {
                   const statusStyle = statusClasses[statusKey] || {
                       bg: "bg-gray-100",
                       text: "text-gray-700",
                       dot: "bg-gray-500",
                   };
                   return (
                       <button
                       key={statusKey}
                       onClick={() => handleStatusClick(statusKey)}
                       disabled={isLoading}
                       className={`flex items-center justify-center w-full text-left px-3 py-2 rounded-lg text-xs font-medium border transition-all duration-200 hover:brightness-95 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed ${
                           selectedStatus === statusKey
                           ? `ring-2 ring-offset-1 ${statusStyle.text.replace(
                               "text-",
                               "ring-"
                           )}`
                           : "border-gray-200"
                       } ${statusStyle.bg} ${statusStyle.text}`}
                       >
                       <span
                           className={`h-1.5 w-1.5 rounded-full ${statusStyle.dot} mr-2`}
                       ></span>
                       {formatStatusLabel(statusKey)}
                       </button>
                   );
                   })}
               </div>

              {/* Comment/Reason Box (Conditional) */}
              <AnimatePresence>
                 {showCommentBox && (
                   <motion.div
                     initial={{ height: 0, opacity: 0, marginTop: 0 }}
                     animate={{ height: "auto", opacity: 1, marginTop: "1rem" }}
                     exit={{ height: 0, opacity: 0, marginTop: 0 }}
                     transition={{ duration: 0.3 }}
                     className="overflow-hidden"
                   >
                     <label
                       htmlFor="commentOrReason"
                       className="block text-xs font-medium text-gray-700 mb-1"
                     >
                       {commentLabel} <span className="text-red-500">*</span>
                     </label>
                     <textarea
                       id="commentOrReason"
                       rows={3}
                       value={commentOrReason}
                       onChange={(e) => setCommentOrReason(e.target.value)}
                       placeholder={commentPlaceholder}
                       className={`w-full p-2 border rounded-md text-xs focus:outline-none focus:ring-2 ${
                         error &&
                         !commentOrReason.trim() &&
                         (selectedStatus === "hold" ||
                           selectedStatus === "rejected")
                           ? "border-red-500 ring-red-300"
                           : "border-gray-300 focus:ring-blue-400"
                       }`}
                       disabled={isLoading}
                     />
                   </motion.div>
                 )}
               </AnimatePresence>

              {/* Error Message Display */}
               {error && (
                   <p className="text-red-600 text-xs mt-2 text-center flex items-center justify-center gap-1">
                   <WarningIcon className="w-4 h-4 " />
                   {error}
                   </p>
               )}

              {/* Action Buttons */}
               <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-gray-200">
                   <button
                   onClick={onClose}
                   disabled={isLoading}
                   className="px-4 py-1.5 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-xs disabled:opacity-60"
                   >
                   Cancel
                   </button>
                   <button
                   onClick={handleConfirm} // This now might trigger confirmation first
                   disabled={isConfirmButtonDisabled}
                   className={`px-5 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs font-medium flex items-center justify-center min-w-[80px] disabled:opacity-60 disabled:cursor-not-allowed`}
                   >
                   {isLoading ? (
                       <AiOutlineLoading3Quarters className="animate-spin h-4 w-4" />
                   ) : (
                       <>
                       <CheckCircleIcon className="w-4 h-4 mr-1" /> Confirm
                       </>
                   )}
                   </button>
               </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default StatusChangePopup;